import {getWaveformByNumber, getWaveformByNumberSync, getImaginaryReal, getImaginaryRealSync} from 'constants/waveforms.js';
import OptionParameter from 'parameters/OptionParameter.js';
import PolyParameter from 'parameters/PolyParameter.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Tone from 'tone';
const WAVEFORMS =
      {
          0: 'sine',
          0.2: 'triangle',
          0.4: 'square',
          0.6: 'sawtooth',
          0.8: 'pulse',
          1: 'fatsine',
          1.2: 'fattriangle',
          1.4: 'fatsquare',
          1.8: 'fatsawtooth',
      };

window.lpf = 1000;
window.len = Math.pow(2, 10);
window.start = 44100*2;

export default superclass => class extends superclass  {
    setupWaveformParameters() {
        
        this.customOscillatorTypeParameter = new AbstractParameter(
            'Custom Oscillator Type', this,
            (o, x) => {
                if (!x) {
                    return;
                }

                if (this.oscillatorTypeParameter) {
                    this.oscillatorType = null;
                }

                o._voices.forEach(
                    v => v.oscillator.type = 'sine');

                Tone.Master.context.isOffline ?
                    x && this.setPeriodicWave(getImaginaryRealSync(x)) :
                    x && getImaginaryReal(x).then(
                        coefs => this.setPeriodicWave(coefs));
            },
            {
                min: 0,
                max: 2,
                unit: '',
                defaultValue: null,
                uxAutomatable: false
            });

        this.registerParameter(this.customOscillatorTypeParameter);
        this.oscillatorTypeParameter = new OptionParameter(
            'Oscillator Type', this,
            (o, x) => {
                if (x !== undefined) {
                    let type = o.oscillatorTypeParameter ?
                        o.oscillatorTypeParameter.getOptionFromNumber(x) :
                        WAVEFORMS[x];
                    this.customOscillatorType = null;
                    type = type || x;
                    if (type && type.includes("fat")) {
                        o._voices.forEach(
                            v => v.oscillator.type = 'sine');
                    }
                    if (type !== o.voices[o.voices.length - 1].oscillator.type) {
                        o.voices.forEach(
                            v => v.oscillator.type = type);
                        if (type === 'pulse') {
                            this.pulseWidthParameter.setAudioParams(
                                o.voices.map(v => v.oscillator.width));
                        } else if (this.pulseWidthParameter) {
                            if (this.pulseWidthParameter._audioParams) {
                                let params = [... this.pulseWidthParameter._audioParams];
                                params.forEach(
                                    p => 
                                        this.pulseWidthParameter.removeAudioParam(
                                            p));
                            }
                        }
                    }
                }
            },
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 'sine',
                options: WAVEFORMS,
                uxAutomatable: false
            });

        this.registerParameter(this.oscillatorTypeParameter);

        this.harmonicsParameter = new AbstractParameter(
            'Harmonics', this,
            (o, x) => {
                if (o.sequencerEngine.isOffline) {
                    o.updateHarmonicsSync(x);
                } else {
                    o.updateHarmonics(x);
                }
            },
            {
                min: -1,
                max: 1,
                unit: '',
                defaultValue: 0,
                uxAutomatable: false
            });

        this.registerParameter(this.harmonicsParameter);

        this.partialCountParameter = new AbstractParameter(
            'Partial Count', this,
            (o, x) => {
                if (!this.customOscillatorType) {
                    o.voices.forEach(
                        v => v.oscillator.partialCount =  x);
                }
            },
            {
                min: 0,
                automatable: false,
                max: 24,
                unit: '',
                defaultValue: 0,
                uxAutomatable: false
            });

        this.registerParameter(this.partialCountParameter);

        this.partialsParameter = new AbstractParameter(
            'Partials',
            this, (o, x) => x && o.voices.forEach(
                v => v.oscillator.partials = x),
            {
                unit: '',
                defaultValue: null,
                automatable: false,
                uxAutomatable: false
            });
        this.registerParameter(this.partialsParameter);

        this.pulseWidthParameter = new PolyParameter(
            'Pulse Width',
            undefined, {
                unit: '',
                defaultValue: 0.1,
                min: 0,
                max: 1,
                automatable: false,
                uxAutomatable: false
            });
        this.registerParameter(this.pulseWidthParameter);

        this.waveformAParameter = new AbstractParameter(
            'Waveform A',
            this, (o, val) => {
                if (o.sequencerEngine.isOffline) {
                    o.updateHarmonicsSync();
                } else {
                    o.updateHarmonics();
                }
            }, {
                defaultValue: 2, // default will be SQUARE
                min: 0,
                max: 50,
            }
        );
        this.registerParameter(this.waveformAParameter);

        this.waveformBParameter = new AbstractParameter(
            'Waveform B',
            this, (o, val) => {
                if (o.sequencerEngine.isOffline) {
                    o.updateHarmonicsSync();
                } else {
                    o.updateHarmonics();
                }
            }, {
                defaultValue: 0, // default will be SQUARE
                min: 0,
                max: 50,
            }
        );
        this.registerParameter(this.waveformBParameter);
    }

    // todo: gonna need to deal with this for freezing
    async updateHarmonics(x=this.harmonicsParameter.get()) {
        if (!this.customOscillatorType || !this.periodicWave) {
            return;
        } 
        let periodicWave = {imag: [... this.periodicWave.imag], real: [... this.periodicWave.real]};
        let waveA = await getWaveformByNumber(this.waveformAParameter.get());
        let waveB = await getWaveformByNumber(this.waveformBParameter.get());
        let otherWave = x > 0 ? waveB : waveA;
        this._updateHarmonics(x, periodicWave, otherWave);
    }

    updateHarmonicsSync(x=this.harmonicsParameter.get()) {
        if (!this.customOscillatorType || !this.periodicWave) {
            return;
        } 
        let periodicWave = {imag: [... this.periodicWave.imag], real: [... this.periodicWave.real]};
        let otherWave = x > 0 ? getWaveformByNumberSync(this.waveformBParameter.get()) :
            getWaveformByNumberSync(this.waveformAParameter.get());
        this._updateHarmonics(x, periodicWave, otherWave);
    }

    _updateHarmonics(harmonics, periodicWave, otherWave) {
        harmonics = Math.abs(harmonics);
        for (let i=0; i < periodicWave.real.length; i++) {
            // make it more saw like...
            let real = periodicWave.real[i];
            let _real = otherWave.real[i];
            periodicWave.real[i] = real + (_real - real)*Math.pow(harmonics, 2);
            let imag = periodicWave.imag[i];
            let _imag = otherWave.imag[i];
            periodicWave.imag[i] = imag + (_imag - imag)*Math.pow(harmonics, 2);
        }
        this.setPeriodicWave(periodicWave, true);
    }

    setPeriodicWave(coefs, isHarmonics=false) {
        let a = new Date().getTime();
        if (!isHarmonics) {
            this.periodicWave = coefs;
        } 
	var periodicWave = Tone.Master.context.createPeriodicWave(
            new Float32Array(coefs.real), new Float32Array(coefs.imag));
        if (!this._voices) {
            return;
        }
        this._voices.forEach(
            voice => {
                voice.oscillator._wave = periodicWave;
                voice._wave = periodicWave;
                voice.oscillator._oscillator._wave = periodicWave;
		voice.oscillator._type = "custom";
		voice.oscillator._oscillator._type = "custom";
                if (voice.oscillator._oscillator._oscillator) {
                    voice.oscillator._oscillator._oscillator.setPeriodicWave(periodicWave);
                }
            }
        );

        // after all of this reseet the harmonics
        if (!isHarmonics) {
            this.harmonics = this.harmonics;
        }
        let b = new Date().getTime();
    }

    setOscillatorSample(sample) {
        /*
        // we need to take this sample, fft it w/ a lp filter,
        // and call setPeriodWave w the fft
        let fft = new dsp.DFT(window.len, Tone.Master.context.sampleRate);
        let type = dsp.DSP.LOWPASS;
        let filter = new dsp.IIRFilter(type, window.lpf, 1, Tone.Master.context.sampleRate);
        let buffer = new Float32Array(window.len);
        sample.audioBuffer.copyFromChannel(
            buffer, 0, window.start);
        console.log('dsp.LOWPASS', type);
        console.log('buffer=', buffer);
        filter.process(buffer);
        console.log('buffer=', buffer);
        fft.forward(buffer);
        console.log(fft.spectrum, fft);
        this.setPeriodicWave(
            {
                real: fft.real,
                imag: fft.imag
            }
        );
        */
        
    }

    hasCustomWaveform() {
        return this.partialsParameter.get() !== null;
    }

    get oscillatorType() {
        return this.oscillatorTypeParameter.get(true);
    }

    get customOscillatorType() {
        return this.customOscillatorTypeParameter.get(true);
    }

    set customOscillatorType(custom) {
        this.customOscillatorTypeParameter.set(custom, true, true);
    }

    set oscillatorType(type) {
        this.oscillatorTypeParameter.set(type, true, true);
        if (type === 'custom') {
            this.partials = [1,1,1,1,1,1,0,1,0,1,0,1].concat(Array(32).fill(0));
        }
    }

    set partials(x) {
        if (this.oscillatorType !== 'custom') {
            this.oscillatorType = 'custom';
        }
        this.partialsParameter.set(x, true, true);
    }

    get partials() {
        return this.partialsParameter.get(true);
    }
}
